import { manifestFieldBox } from "components/Document/ReviewDocumentMetadataDialog/components/manifestFieldBoxes";
import { boxRegion, withBottomBuffer } from "components/Document/ReviewInteraction";
import { GENERATOR_STATUS_CORRECTION_FIELDS } from "encamp-shared/src/waste/discrepancyFieldMeta";
import { formatWasteQuantity } from "encamp-shared/src/waste/eManifestWasteUnit";
import {
  GeneratorStatusCorrectionReviewFieldsQuery,
  GeneratorStatusExceedanceCorrectableWasteLinesQuery,
} from "generated-graphql/graphql";

type DocumentManifest = GeneratorStatusCorrectionReviewFieldsQuery["documentManifestDetail"];
export type CorrectionReviewLines =
  GeneratorStatusExceedanceCorrectableWasteLinesQuery["generatorStatusExceedanceCorrectableWasteLines"];

export type CorrectionSelection = { field: string; lineId: string; editing?: boolean };

export function selectedCorrectionField({
  selection,
  lines,
  documentManifest,
  generatorId,
}: {
  selection: CorrectionSelection;
  lines: CorrectionReviewLines;
  documentManifest?: DocumentManifest;
  generatorId?: string | null;
}) {
  const field = GENERATOR_STATUS_CORRECTION_FIELDS.find((item) => item === selection.field);
  if (!field) return null;
  if (field === "generatorId") {
    return {
      field,
      name: field,
      lineId: null,
      group: "Manifest",
      value: generatorId,
      wasteLineNumber: null,
    };
  }
  const line = lines.find((item) => item.id === selection.lineId);
  if (!line) return null;
  const documentLines = [...(documentManifest?.wasteLines ?? [])].sort((a, b) =>
    a.wasteLineNumber.localeCompare(b.wasteLineNumber, undefined, { numeric: true })
  );
  const documentIndex = documentLines.findIndex(
    (item) => item.wasteLineNumber === line.wasteLineNumber
  );
  const sourceLine = documentLines[documentIndex];
  const name = field === "quantityUnitOfMeasureCode" ? "qtyUnitOfMeasureCode" : field;
  return {
    field,
    name: documentIndex < 0 ? undefined : `wasteLines.${documentIndex}.${name}`,
    lineId: line.id,
    group: `Line ${line.wasteLineNumber}`,
    value: field === "quantity" ? line.documentAmount : sourceLine?.[name],
    documentQuantity:
      field === "quantity" && sourceLine?.quantity != null
        ? formatWasteQuantity(sourceLine.quantity, sourceLine.qtyUnitOfMeasureCode)
        : undefined,
    wasteLineNumber: line.wasteLineNumber,
  };
}

export type SelectedCorrectionField = ReturnType<typeof selectedCorrectionField>;

/** Follow the correction selection, including before its value input receives focus. */
export function correctionSelectionHighlight({
  selected,
  reading,
  pageCount,
}: {
  selected: SelectedCorrectionField;
  reading?: NonNullable<DocumentManifest>["reading"];
  pageCount: number | null;
}) {
  if (!selected?.name) return null;
  const field = manifestFieldBox(selected.name);
  if (!field) return null;
  const region = boxRegion(field.variant, field.box, field.rowIndex);
  if (!region) return null;
  const linePage = reading?.wasteLines.find(
    (line) => line.wasteLineNumber === selected.wasteLineNumber
  )?.pageNumber;
  const mainPage = reading?.manifestPageNumber ?? (pageCount == null ? null : 1);
  const fallbackPage = mainPage == null ? null : mainPage + (field.variant === "8700-22A" ? 1 : 0);
  const page = linePage ?? fallbackPage;
  if (page == null) return null;
  return {
    page: pageCount == null ? page : Math.min(Math.max(page, 1), pageCount),
    region: withBottomBuffer(region),
  };
}
